import type { WorldEntrancePhase } from '@viewer/playcanvas/WorldEntrance';

export type EntranceRevealMode = 'shader';
export type EntranceGraphicsApi = 'webgpu' | 'webgl2';
export type EntranceGsplatRenderer = 'compute' | 'raster-gpu' | 'raster-cpu' | 'auto';

export function resolveEntranceRevealMode(state: {
  graphicsApi: EntranceGraphicsApi;
  gsplatRenderer: EntranceGsplatRenderer;
}): EntranceRevealMode {
  void state;
  return 'shader';
}

export interface PreviewDotsGateState {
  welcomeDismissed: boolean;
  hasPcvPreview: boolean;
  hasPointDots: boolean;
  hasWorldEntrance: boolean;
  entrancePhase: WorldEntrancePhase;
  hasGsplatEntity: boolean;
  requireSplatEntity: boolean;
}

export function canStartPreviewDots(state: PreviewDotsGateState): boolean {
  if (!state.welcomeDismissed) return false;
  if (!state.hasPcvPreview || state.hasPointDots || !state.hasWorldEntrance) return false;
  if (
    state.entrancePhase !== 'welcome' &&
    state.entrancePhase !== 'dots' &&
    state.entrancePhase !== 'loading'
  ) {
    return false;
  }
  if (state.requireSplatEntity && !state.hasGsplatEntity) return false;
  return true;
}

export interface WarmEntranceFallbackState {
  sessionWarm: boolean;
  hasAssetReady: boolean;
  hasRenderedSplats: boolean;
  elapsedMs: number;
  verifyWindowMs: number;
}

export function shouldFallbackWarmEntrance(state: WarmEntranceFallbackState): boolean {
  if (!state.sessionWarm) return false;
  if (state.hasAssetReady && state.hasRenderedSplats) return false;
  return state.elapsedMs >= state.verifyWindowMs;
}

export interface RevealReleaseGateState {
  renderedSplats: number;
  minRenderedSplats: number;
  hasGsplatEntity: boolean;
  hasAssetReady: boolean;
  hasRevealShader: boolean;
  isPointMorphDone: boolean;
  streamingReady: boolean;
  allowStreamingTimeout?: boolean;
  streamWaitMs?: number;
  streamTimeoutMs?: number;
}

export function shouldAllowStreamingRevealTimeout(state: {
  isStreamedSplat: boolean;
  showTouchEntranceHints: boolean;
}): boolean {
  return state.isStreamedSplat && !state.showTouchEntranceHints;
}

const STREAMING_PRE_REVEAL_PROGRESS_CAP = 0.96;
const STREAMING_READY_PROGRESS_CAP = 0.99;

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

export function resolveEntranceDisplayProgress(state: {
  entranceProgress: number;
  streamingFill: number;
  streamingDownloadProgress?: number;
  streamingReady: boolean;
  isStreamedSplat: boolean;
  entrancePhase: WorldEntrancePhase;
}): number {
  const entranceProgress = clamp01(state.entranceProgress);
  if (state.entrancePhase === 'reveal' || state.entrancePhase === 'done') return entranceProgress;
  if (!state.isStreamedSplat) return entranceProgress;

  const streamingFill = clamp01(state.streamingFill);
  if (state.streamingReady) {
    return STREAMING_READY_PROGRESS_CAP;
  }
  if (
    typeof state.streamingDownloadProgress === 'number' &&
    Number.isFinite(state.streamingDownloadProgress)
  ) {
    return Math.min(STREAMING_PRE_REVEAL_PROGRESS_CAP, clamp01(state.streamingDownloadProgress));
  }

  void entranceProgress;
  return Math.min(
    STREAMING_PRE_REVEAL_PROGRESS_CAP,
    streamingFill * STREAMING_PRE_REVEAL_PROGRESS_CAP,
  );
}

export function canReleaseReveal(state: RevealReleaseGateState): boolean {
  if (!state.hasGsplatEntity || !state.hasAssetReady || !state.hasRevealShader) return false;
  if (!state.isPointMorphDone) return false;
  if (!state.streamingReady) {
    const timeoutReady =
      Boolean(state.allowStreamingTimeout) &&
      (state.streamWaitMs ?? 0) >= (state.streamTimeoutMs ?? Infinity) &&
      state.renderedSplats >= state.minRenderedSplats;
    if (!timeoutReady) return false;
  }
  return true;
}

export interface RevealWaveTiming {
  effectTime: number;
  delay: number;
  speed: number;
  acceleration: number;
}

export function revealLiftWaveRadius(timing: RevealWaveTiming): number {
  const t = Math.max(0, timing.effectTime - Math.max(0, timing.delay));
  if (t <= 0) return 0;
  return Math.max(0, timing.speed * t + 0.5 * timing.acceleration * t * t);
}

export interface PreviewDissolveWaveTiming extends RevealWaveTiming {
  /**
   * Keep preview dots visible until the lift wave has travelled this far past
   * them. The shader only reaches full-size colour behind the wave front.
   */
  trailRadius: number;
}

export function previewDissolveWaveRadius(timing: PreviewDissolveWaveTiming): number {
  return Math.max(0, revealLiftWaveRadius(timing) - Math.max(0, timing.trailRadius));
}

export interface FullPreviewDissolveGateState {
  effectTime: number;
  completionTime: number;
  overlapTailSeconds: number;
}

export function canFullyDissolvePreviewDots(state: FullPreviewDissolveGateState): boolean {
  return state.effectTime >= state.completionTime - Math.max(0, state.overlapTailSeconds);
}

export interface WorldReadyGateState {
  loadStateReady: boolean;
  hasRenderedSplats: boolean;
  entranceDone: boolean;
  collisionRequired: boolean;
  collisionLoaded: boolean;
}

export function canUseWorld(state: WorldReadyGateState): boolean {
  if (!state.loadStateReady || !state.hasRenderedSplats || !state.entranceDone) return false;
  if (state.collisionRequired && !state.collisionLoaded) return false;
  return true;
}

export function latchWorldReady(previouslyReady: boolean, state: WorldReadyGateState): boolean {
  return previouslyReady || canUseWorld(state);
}
